// /app/building — the demo's Building Management page: every development's Building management tab at once, four
// sub-tabs (?sub=), each behind its own permission page.
//   overview    the portfolio at a glance (buildingRecord, assets or contracts; GET /building/overview)
//   plant       every asset (assets) — the old Assets list, with a Development column
//   contracts   every supplier contract (contracts) — with the notice / Section 20 tiles (?filter=)
//   budgets     every development's budget & charges (budgets)
// A sub-tab shows only to someone who can read its page (visibility follows the permissions; the data follows the user's
// scope); with one sub-tab there is no tab row, and with none the page says so. /app/assets, /app/contracts and /app/budgets
// redirect here (App.tsx); an asset keeps its own page at /app/assets/:id.
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { RequirePermission } from '../components/RequirePermission';
import { Tabs, PageHeader, type MenuEntry } from '../components/ui';
import { AssetForm } from '../components/forms/AssetForm';
import { PlantPanel } from '../components/property/PlantPanel';
import { ContractsPanel } from '../components/building/ContractsPanel';
import { BuildingOverviewTab } from '../components/portfolio/BuildingOverviewTab';
import { BuildingBudgetsTab } from '../components/portfolio/BuildingBudgetsTab';

function PlantTab() {
  const [adding, setAdding] = useState(false);
  return (
    <>
      <PageHeader title="Plant & equipment" menu={[
        { label: 'Add asset', icon: 'Plus', can: 'assets.create', onClick: () => setAdding(true) },
        { label: 'Export CSV', icon: 'Download', export: { path: '/assets', page: 'assets', query: {}, filename: 'assets' } },
      ]} />
      <PlantPanel onAdd={() => setAdding(true)} />
      {adding && <AssetForm open onClose={() => setAdding(false)} />}
    </>
  );
}

function ContractsTab() {
  const [menu, setMenu] = useState<MenuEntry[]>([]);
  const [params, setParams] = useSearchParams();
  return (
    <>
      <PageHeader title="Supplier contracts" menu={menu} />
      <ContractsPanel openId={params.get('contract')} onMenu={setMenu}
        onOpen={(id) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set('contract', id); else n.delete('contract'); return n; }, { replace: true })} />
    </>
  );
}

const BUILDING_SUBS = [
  { key: 'overview', label: 'Overview', pages: ['buildingRecord', 'assets', 'contracts'], Tab: BuildingOverviewTab },
  { key: 'plant', label: 'Plant', pages: ['assets'], Tab: PlantTab },
  { key: 'contracts', label: 'Contracts', pages: ['contracts'], Tab: ContractsTab },
  { key: 'budgets', label: 'Budgets', pages: ['budgets'], Tab: BuildingBudgetsTab },
] as const;
type BuildingSub = (typeof BUILDING_SUBS)[number]['key'];

export default function BuildingPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const subs = BUILDING_SUBS.filter((s) => s.pages.some((p) => can(`${p}.read`)));
  const sub = subs.find((s) => s.key === params.get('sub')) ?? subs[0];
  // Switching sub-tab drops the other one's state (?filter=, ?contract=, ?bmf=…).
  const go = (k: BuildingSub) => setParams(k === subs[0]?.key ? {} : { sub: k });
  if (!sub) return <RequirePermission page="buildingRecord">{null}</RequirePermission>;
  const { Tab } = sub;
  return (
    <div>
      {subs.length > 1 && (
        <Tabs className="mb-2.5" items={subs.map((s) => ({ key: s.key, label: s.label }))} value={sub.key} onChange={go} />
      )}
      <Tab key={sub.key} />
    </div>
  );
}
